/**
 * Compare Component
 * Rich comparison view between two anime users.
 * All computation is server-side; this component handles UI and data flow.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAniList } from '../context/AniListContext';
import { useAlert } from '../context/ToastContext';
import { useLinkSettings } from '../context/LinkSettingsContext';
import { fetchPublicUserList, addAnimeWithRetry } from '../services/aniListService';
import { getCreativeError, getCreativeUserNotFound } from '../utils/errorMessages';
import {
    runComparison,
    getSavedComparisons,
    getSavedComparison,
    refreshComparison,
    deleteSavedComparison
} from '../services/apiService';
import { useTitlePreference } from '../context/TitlePreferenceContext';
import { getDisplayTitle } from '../hooks/useDisplayTitle';
import SiteLinksModal from './SiteLinksModal';
import './Compare.css';

// ============================================================
// SORT OPTIONS
// ============================================================
const SORT_OPTIONS = [
    { key: 'averageScore', label: 'Avg Score' },
    { key: 'name', label: 'Name' },
    { key: 'userAScore', label: 'A\'s Score' },
    { key: 'userBScore', label: 'B\'s Score' }
];

// ============================================================
// HELPER: Get display title  (reactive via TitlePreferenceContext)
// ============================================================
function getTitle(media, titlePreference) {
    return getDisplayTitle(media, titlePreference);
}

// ============================================================
// HELPER: Format number
// ============================================================
function formatNumber(n) {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
    return String(n);
}

// ============================================================
// HELPER: Sort anime list (titlePreference required for sort by name)
// ============================================================
function sortAnimeList(list, sortKey, sortDir, titlePreference) {
    return [...list].sort((a, b) => {
        let va, vb;
        switch (sortKey) {
            case 'name':
                va = getTitle(a.media, titlePreference).toLowerCase();
                vb = getTitle(b.media, titlePreference).toLowerCase();
                return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
            case 'userAScore':
                va = a.userAScore || 0;
                vb = b.userAScore || 0;
                break;
            case 'userBScore':
                va = a.userBScore || 0;
                vb = b.userBScore || 0;
                break;
            case 'scoreDiff':
                va = a.scoreDiff || 0;
                vb = b.scoreDiff || 0;
                break;
            case 'averageScore':
            default:
                va = a.averageScore || a.media?.averageScore || 0;
                vb = b.averageScore || b.media?.averageScore || 0;
                break;
        }
        return sortDir === 'asc' ? va - vb : vb - va;
    });
}

// ============================================================
// ANIME CARD — Discover-style (3 per row, bigger, with AniList link)
// ============================================================
const AnimeCard = React.memo(({ anime, userAName, userBName, showScores = true, onClick, onAdd, isAdding, userAnimeMap, onWatch }) => {
    const { titlePreference } = useTitlePreference();
    const coverUrl = anime.media?.coverImage?.extraLarge || anime.media?.coverImage?.large || anime.media?.coverImage?.medium;
    const title = getTitle(anime.media, titlePreference);
    const anilistUrl = anime.media?.id ? `https://anilist.co/anime/${anime.media.id}` : null;
    const year = anime.media?.seasonYear || anime.media?.startDate?.year;
    const [showAddMenu, setShowAddMenu] = React.useState(false);
    const addRef = React.useRef(null);
    const alreadyInList = userAnimeMap?.has(anime.media?.id);

    React.useEffect(() => {
        if (!showAddMenu) return;
        const close = (e) => { if (addRef.current && !addRef.current.contains(e.target)) setShowAddMenu(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [showAddMenu]);

    return (
        <div className="compare-anime-card" onClick={onClick}>
            <div className="compare-anime-cover">
                {coverUrl ? <img src={coverUrl} alt="" loading="lazy" /> : <div className="compare-anime-no-cover">?</div>}
            </div>
            <div className="compare-anime-info">
                <div className="compare-anime-title" title={title}>
                    {anilistUrl ? (
                        <a href={anilistUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                            {title}
                        </a>
                    ) : title}
                </div>
                <div className="compare-anime-meta">
                    {year && <span>📅 {year}</span>}
                    {anime.media?.episodes && <span>🎬 {anime.media.episodes} eps</span>}
                    {anime.media?.averageScore > 0 && <span>⭐ {(anime.media.averageScore / 10).toFixed(1)}</span>}
                </div>
                {showScores && (anime.userAScore > 0 || anime.userBScore > 0) && (
                    <div className="compare-anime-scores">
                        {anime.userAScore > 0 && <span className="score-a" title={userAName}>{userAName}: {anime.userAScore}</span>}
                        {anime.userBScore > 0 && <span className="score-b" title={userBName}>{userBName}: {anime.userBScore}</span>}
                    </div>
                )}
                {anime.media?.genres?.length > 0 && (
                    <div className="compare-anime-genres">
                        {anime.media.genres.slice(0, 3).map((g, i) => (
                            <span key={i} className="compare-genre-tag">{g}</span>
                        ))}
                    </div>
                )}
                {/* Action buttons row */}
                <div className="compare-card-actions">
                    {onWatch && (
                        <button
                            className="compare-card-watch-btn"
                            onClick={(e) => { e.stopPropagation(); onWatch(); }}
                            title="Watch"
                        >
                            🎬
                        </button>
                    )}
                    {onAdd && !alreadyInList && (
                        <div className="compare-card-add-wrapper" ref={addRef}>
                            <button
                                className={`compare-card-add-btn ${isAdding ? 'loading' : ''}`}
                                onClick={(e) => { e.stopPropagation(); if (!isAdding) setShowAddMenu(!showAddMenu); }}
                                title="Add to list"
                                disabled={isAdding}
                            >
                                {isAdding ? '⏳' : '➕'}
                            </button>
                            {showAddMenu && (
                                <div className="compare-card-add-menu">
                                    <button onClick={(e) => { e.stopPropagation(); onAdd('PLANNING'); setShowAddMenu(false); }}>📅 Planning</button>
                                    <button onClick={(e) => { e.stopPropagation(); onAdd('CURRENT'); setShowAddMenu(false); }}>▶️ Watching</button>
                                    <button onClick={(e) => { e.stopPropagation(); onAdd('COMPLETED'); setShowAddMenu(false); }}>✅ Completed</button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});

// ============================================================
// ANIME DETAIL MODAL (like Discover tab)
// ============================================================
const AnimeDetailModal = ({ anime, onClose, userAName, userBName, onAdd, isAdding, userStatus, onWatch }) => {
    const { titlePreference } = useTitlePreference();
    const [showAddMenu, setShowAddMenu] = React.useState(false);
    const addRef = React.useRef(null);

    React.useEffect(() => {
        if (!showAddMenu) return;
        const close = (e) => { if (addRef.current && !addRef.current.contains(e.target)) setShowAddMenu(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [showAddMenu]);

    if (!anime) return null;
    const media = anime.media || {};
    const title = getTitle(media, titlePreference);
    const altTitle = media.title?.english && media.title?.romaji && media.title.english !== media.title.romaji
        ? media.title.romaji : null;

    const statusLabels = { CURRENT: '▶️ Watching', PLANNING: '📅 Planning', COMPLETED: '✅ Completed', PAUSED: '⏸️ Paused', DROPPED: '🗑️ Dropped', REPEATING: '🔄 Repeating' };

    return (
        <div className="anime-modal-overlay" onClick={onClose}>
            <div className="anime-modal compare-detail-modal" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>✕</button>

                <div className="modal-content">
                    <div className="modal-header">
                        {media.coverImage?.extraLarge && (
                            <img src={media.coverImage.extraLarge} alt="" className="modal-cover" />
                        )}
                        <div className="modal-header-info">
                            <h2>
                                <a href={`https://anilist.co/anime/${media.id}`} target="_blank" rel="noopener noreferrer">
                                    {title}
                                </a>
                            </h2>
                            {altTitle && <p className="alt-title">{altTitle}</p>}
                            <p className="anime-meta">
                                {[media.format, media.episodes ? `${media.episodes} eps` : null, media.status].filter(Boolean).join(' • ')}
                            </p>
                            {media.averageScore > 0 && (
                                <div className="score-large">⭐ {(media.averageScore / 10).toFixed(1)}/10</div>
                            )}
                            {onWatch && (
                                <button className="modal-watch-btn" onClick={onWatch}>
                                    🎬 Watch
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Add to List / Status section */}
                    {onAdd && (
                        <div className="compare-modal-add-section" ref={addRef}>
                            {userStatus ? (
                                <div className="compare-modal-status-badge">
                                    <span className="compare-modal-status-label">In Your List:</span>
                                    <span className="compare-modal-status-value">{statusLabels[userStatus] || userStatus}</span>
                                </div>
                            ) : (
                                <div className="compare-modal-add-wrapper">
                                    <button
                                        className={`compare-modal-add-btn ${isAdding ? 'loading' : ''} ${showAddMenu ? 'open' : ''}`}
                                        onClick={() => { if (!isAdding) setShowAddMenu(!showAddMenu); }}
                                        disabled={isAdding}
                                    >
                                        <span className="add-to-list-emoji">➕</span>
                                        <span className="add-to-list-label">{isAdding ? 'Adding...' : 'Add to List'}</span>
                                        <span className="add-to-list-arrow">{showAddMenu ? '▲' : '▼'}</span>
                                    </button>
                                    {showAddMenu && (
                                        <div className="compare-modal-add-dropdown">
                                            <button onClick={() => { onAdd('PLANNING'); setShowAddMenu(false); }}>📅 Add to Planning</button>
                                            <button onClick={() => { onAdd('CURRENT'); setShowAddMenu(false); }}>▶️ Add to Watching</button>
                                            <button onClick={() => { onAdd('COMPLETED'); setShowAddMenu(false); }}>✅ Add to Completed</button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* User scores */}
                    {(anime.userAScore > 0 || anime.userBScore > 0) && (
                        <div className="modal-user-scores">
                            {anime.userAScore > 0 && <span className="modal-score-a">{userAName}: {anime.userAScore}/10</span>}
                            {anime.userBScore > 0 && <span className="modal-score-b">{userBName}: {anime.userBScore}/10</span>}
                        </div>
                    )}

                    {media.description && (
                        <div className="modal-description">
                            <h3>Description</h3>
                            <div dangerouslySetInnerHTML={{ __html: media.description }} />
                        </div>
                    )}

                    {media.genres?.length > 0 && (
                        <div className="modal-genres">
                            <h3>Genres</h3>
                            <div className="genre-list">
                                {media.genres.map((g, i) => <span key={i} className="genre-tag-large">{g}</span>)}
                            </div>
                        </div>
                    )}

                    <div className="modal-actions">
                        <a href={`https://anilist.co/anime/${media.id}`} target="_blank" rel="noopener noreferrer" className="btn-anilist">
                            View on AniList
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================
// OVERLAY for "View All" anime — Discover-style grid
// ============================================================
const AnimeListOverlay = ({ title, animeList, onClose, userAName, userBName, showScores = true, onAdd, addingAnime, userAnimeMap, onWatch }) => {
    const { titlePreference } = useTitlePreference();
    const [sortKey, setSortKey] = useState('averageScore');
    const [sortDir, setSortDir] = useState('desc');
    const [detailAnime, setDetailAnime] = useState(null);

    const sorted = useMemo(() => sortAnimeList(animeList, sortKey, sortDir, titlePreference), [animeList, sortKey, sortDir, titlePreference]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    return (
        <div className="compare-overlay-backdrop" onClick={onClose}>
            <div className="compare-overlay compare-overlay-wide" onClick={e => e.stopPropagation()}>
                <div className="compare-overlay-header">
                    <h3>{title} ({animeList.length})</h3>
                    <button className="compare-overlay-close" onClick={onClose}>✕</button>
                </div>
                <div className="compare-overlay-sort">
                    {SORT_OPTIONS.map(opt => (
                        <button
                            key={opt.key}
                            className={`sort-btn ${sortKey === opt.key ? 'active' : ''}`}
                            onClick={() => toggleSort(opt.key)}
                        >
                            {opt.label} {sortKey === opt.key && (sortDir === 'asc' ? '↑' : '↓')}
                        </button>
                    ))}
                </div>
                <div className="compare-overlay-grid">
                    {sorted.map((anime, i) => (
                        <AnimeCard
                            key={anime.media?.id || i}
                            anime={anime}
                            userAName={userAName}
                            userBName={userBName}
                            showScores={showScores}
                            onClick={() => setDetailAnime(anime)}
                            onAdd={onAdd ? (status) => onAdd(anime.media?.id, status) : undefined}
                            isAdding={addingAnime?.has(anime.media?.id)}
                            userAnimeMap={userAnimeMap}
                            onWatch={onWatch ? () => onWatch(getTitle(anime.media, titlePreference)) : undefined}
                        />
                    ))}
                </div>

                {detailAnime && (
                    <AnimeDetailModal
                        anime={detailAnime}
                        onClose={() => setDetailAnime(null)}
                        userAName={userAName}
                        userBName={userBName}
                        onAdd={onAdd ? (status) => onAdd(detailAnime.media?.id, status) : undefined}
                        isAdding={addingAnime?.has(detailAnime.media?.id)}
                        userStatus={userAnimeMap?.get(detailAnime.media?.id)}
                        onWatch={onWatch ? () => onWatch(getTitle(detailAnime.media, titlePreference)) : undefined}
                    />
                )}
            </div>
        </div>
    );
};

// ============================================================
// SAVED COMPARISONS OVERLAY
// ============================================================
const SavedComparisonsOverlay = ({ comparisons, onLoad, onDelete, activeUsername, onClose }) => {
    return (
        <div className="compare-overlay-backdrop" onClick={onClose}>
            <div className="compare-overlay compare-overlay-narrow" onClick={e => e.stopPropagation()}>
                <div className="compare-overlay-header">
                    <h3>All Saved Comparisons ({comparisons.length})</h3>
                    <button className="compare-overlay-close" onClick={onClose}>✕</button>
                </div>
                <div className="saved-overlay-list">
                    {comparisons.map(saved => (
                        <div
                            key={saved.userBUsername}
                            className={`saved-item ${activeUsername === saved.userBUsername ? 'active' : ''}`}
                            onClick={() => { onLoad(saved.userBUsername); onClose(); }}
                        >
                            <div className="saved-avatar">
                                {saved.userBAvatar ? (
                                    <img src={saved.userBAvatar} alt="" />
                                ) : (
                                    <div className="saved-avatar-placeholder">{saved.userBUsername[0]?.toUpperCase()}</div>
                                )}
                            </div>
                            <div className="saved-info">
                                <div className="saved-name">{saved.userBUsername}</div>
                                <div className="saved-meta">
                                    {saved.summary?.compatibilityScore}% compat · {saved.summary?.commonAnimeCount} common
                                </div>
                            </div>
                            <button className="saved-delete" onClick={(e) => { e.stopPropagation(); onDelete(saved.userBUsername, e); }} title="Delete">✕</button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ============================================================
// MAIN COMPARE COMPONENT
// ============================================================
const Compare = () => {
    const { username, userData, isConnected, token, refreshData, savedComparisons, loadSavedComparisons } = useAniList();
    const { showError } = useAlert();
    const { watchLinks } = useLinkSettings();
    const { titlePreference } = useTitlePreference();

    // Search state
    const [searchUsername, setSearchUsername] = useState('');
    const [isComparing, setIsComparing] = useState(false);
    const [error, setError] = useState(null);

    // Comparison data
    const [comparisonData, setComparisonData] = useState(null);
    const [comparedUsername, setComparedUsername] = useState('');
    const [comparedAvatar, setComparedAvatar] = useState(null);
    const [comparisonStats, setComparisonStats] = useState(null);

    // Saved comparisons overlay (data comes from context)
    const [showSavedOverlay, setShowSavedOverlay] = useState(false);

    // Overlay state
    const [overlayData, setOverlayData] = useState(null);

    // Detail modal state
    const [detailAnime, setDetailAnime] = useState(null);

    // Group detail overlay state
    const [groupDetailData, setGroupDetailData] = useState(null);
    const [viewAllGroups, setViewAllGroups] = useState(null);

    // Add-to-list state
    const [addingAnime, setAddingAnime] = useState(new Set());

    // Watch modal state
    const [showWatchModal, setShowWatchModal] = useState(false);
    const [selectedAnimeForWatch, setSelectedAnimeForWatch] = useState(null);

    // Build user anime map to check existing list status (useState for optimistic updates)
    const [userAnimeMap, setUserAnimeMap] = useState(new Map());
    useEffect(() => {
        if (!userData || !userData.lists) return;
        const m = new Map();
        userData.lists.forEach(list => {
            list.entries.forEach(entry => {
                m.set(entry.media.id, entry.status);
            });
        });
        setUserAnimeMap(m);
    }, [userData]);

    const searchInputRef = useRef(null);

    // Get user A's entries
    const userAEntries = useMemo(() => {
        if (!userData?.lists) return [];
        return userData.lists.flatMap(list => list.entries || []);
    }, [userData]);

    // Run comparison
    const handleCompare = useCallback(async (targetUsername) => {
        const target = targetUsername || searchUsername.trim();
        if (!target) return;
        if (!username) {
            setError('Please connect to AniList first');
            return;
        }
        if (target.toLowerCase() === username.toLowerCase()) {
            setError("You can't compare with yourself!");
            return;
        }

        setIsComparing(true);
        setError(null);
        setComparisonData(null);

        try {
            const userBData = await fetchPublicUserList(target);
            if (!userBData || !userBData.lists) {
                throw new Error(`Could not fetch anime list for "${target}"`);
            }

            const userBEntries = userBData.lists.flatMap(list => list.entries || []);
            const userBAvatar = userBData.user?.avatar?.large || userBData.user?.avatar?.medium || null;

            const result = await runComparison(
                username, userAEntries, target, userBEntries, userBAvatar, titlePreference
            );

            setComparisonData(result.metrics);
            setComparedUsername(target);
            setComparedAvatar(userBAvatar);
            setComparisonStats(result.stats);
            loadSavedComparisons();
        } catch (err) {
            console.error('Comparison failed:', err);
            const status = err.response?.status || err?.status;
            if (status === 404 || err.message?.includes('not found') || err.message?.includes('Not Found')) {
                setError(getCreativeUserNotFound(target));
            } else if (err.message?.includes('private') || err.message?.includes('Private')) {
                setError(`🔒 "${target}" has a private profile — can't peek at their list!`);
            } else {
                setError(getCreativeError(err.response?.data?.error || err.message || 'Comparison failed'));
            }
        } finally {
            setIsComparing(false);
        }
    }, [searchUsername, username, userAEntries, loadSavedComparisons, titlePreference]);

    // Load saved comparison
    const handleLoadSaved = useCallback(async (userBUsername) => {
        setIsComparing(true);
        setError(null);
        try {
            const result = await getSavedComparison(username, userBUsername);
            setComparisonData(result.metrics);
            setComparedUsername(userBUsername);
            setComparedAvatar(result.userBAvatar);
            setComparisonStats(null);
            loadSavedComparisons(); // Refresh list so this becomes most recent
        } catch (err) {
            setError(getCreativeError('Failed to load comparison: ' + (err.response?.data?.error || err.message)));
        } finally {
            setIsComparing(false);
        }
    }, [username, loadSavedComparisons]);

    // Refresh saved comparison
    const handleRefresh = useCallback(async () => {
        if (!comparedUsername || !username) return;
        setIsComparing(true);
        setError(null);
        try {
            const userBData = await fetchPublicUserList(comparedUsername);
            const userBEntries = userBData.lists.flatMap(list => list.entries || []);
            const userBAvatar = userBData.user?.avatar?.large || userBData.user?.avatar?.medium || null;
            const result = await refreshComparison(
                username, comparedUsername, userAEntries, userBEntries, userBAvatar, titlePreference
            );

            setComparisonData(result.metrics);
            setComparedAvatar(userBAvatar);
            setComparisonStats(result.stats);
            loadSavedComparisons();
        } catch (err) {
            setError(getCreativeError('Refresh failed: ' + (err.response?.data?.error || err.message)));
        } finally {
            setIsComparing(false);
        }
    }, [comparedUsername, username, userAEntries, loadSavedComparisons, titlePreference]);

    // Delete saved comparison
    const handleDeleteSaved = useCallback(async (userBUsername, e) => {
        if (e) e.stopPropagation();
        try {
            await deleteSavedComparison(username, userBUsername);
            // Refresh saved comparisons from context
            loadSavedComparisons();
            if (comparedUsername === userBUsername) {
                setComparisonData(null);
                setComparedUsername('');
            }
        } catch (err) {
            console.error('Delete failed:', err);
        }
    }, [username, comparedUsername, loadSavedComparisons]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleCompare();
    };

    // Add anime to user's AniList (matches Discover's flow)
    const handleAddToList = useCallback(async (mediaId, status) => {
        if (!token) {
            showError('Authentication required. Please reconnect with OAuth in Settings.', 'Authentication Required');
            return;
        }

        // Optimistic update
        setUserAnimeMap(prev => new Map(prev).set(mediaId, status));
        setAddingAnime(prev => new Set([...prev, mediaId]));

        try {
            await addAnimeWithRetry(mediaId, status, token);
            console.log(`✅ Added anime ${mediaId} as ${status}`);

            // Refresh user data to sync state
            if (refreshData) refreshData(true);

            setAddingAnime(prev => { const next = new Set(prev); next.delete(mediaId); return next; });
        } catch (err) {
            console.error(`❌ Failed to add anime ${mediaId}:`, err);

            // Revert optimistic update on error
            setUserAnimeMap(prev => { const next = new Map(prev); next.delete(mediaId); return next; });
            setAddingAnime(prev => { const next = new Set(prev); next.delete(mediaId); return next; });

            showError(`Failed to add to list: ${err.message}`, 'Add Failed');
        }
    }, [token, refreshData, showError]);

    // ============================================================
    // RENDER: No connection
    // ============================================================
    if (!isConnected) {
        return (
            <div className="compare-container">
                <div className="compare-empty-state">
                    <div className="compare-empty-icon">🔗</div>
                    <h2>Connect to AniList</h2>
                    <p>Connect your AniList account to compare anime lists with other users.</p>
                </div>
            </div>
        );
    }

    const metrics = comparisonData;
    // Most recent saved comparison (for the inline display)
    const mostRecentSaved = savedComparisons.length > 0 ? savedComparisons[0] : null;
    const hasMoreSaved = savedComparisons.length > 1;

    // ============================================================
    // RENDER SECTIONS
    // ============================================================

    const renderCompatibilityHero = () => {
        if (!metrics) return null;
        const score = metrics.compatibilityScore || 0;
        const getLabel = (s) => {
            if (s >= 90) return 'Anime Soulmates — You share one brain! 💞';
            if (s >= 75) return 'Kindred Weebs — Remarkably similar taste! 🔥';
            if (s >= 60) return 'Watch Buddies — Lots in common! 🤝';
            if (s >= 45) return 'Crossing Paths — Some shared favorites 🌟';
            if (s >= 30) return 'Parallel Worlds — Different but interesting 🌈';
            if (s >= 15) return 'Isekai\'d Apart — Rarely the same picks 🌀';
            return 'Multiverse Split — Completely different taste! ⚡';
        };
        const hue = Math.round((score / 100) * 120);

        return (
            <div className="compare-hero" style={{ '--compat-hue': hue }}>
                <div className="compare-hero-score">
                    <div className="hero-ring" style={{ '--progress': `${score}%` }}>
                        <span className="hero-number">{score}%</span>
                    </div>
                </div>
                <div className="compare-hero-label">{getLabel(score)}</div>
                <div className="compare-hero-subtitle">
                    Compatibility between <strong>{username}</strong> and <strong>{comparedUsername}</strong>
                </div>

            </div>
        );
    };

    const renderHeadToHead = () => {
        if (!metrics?.headToHead) return null;
        const h2h = metrics.headToHead;

        return (
            <Section title="Head-to-Head Leaderboard" icon="🏆" id="headtohead">
                <div className="h2h-grid">
                    {h2h.categories.map((cat, i) => (
                        <div key={i} className={`h2h-row ${cat.winner === 'A' ? 'a-wins' : cat.winner === 'B' ? 'b-wins' : 'tie'}`}>
                            <div className="h2h-a-value">{formatNumber(cat.aValue)}</div>
                            <div className="h2h-label">
                                <span className="h2h-icon">{cat.icon}</span>
                                <span>{cat.label}</span>
                            </div>
                            <div className="h2h-b-value">{formatNumber(cat.bValue)}</div>
                        </div>
                    ))}
                </div>
                <div className="h2h-summary">
                    {h2h.overallWinner === 'A' ? `${username} leads ${h2h.aWins}-${h2h.bWins}` :
                        h2h.overallWinner === 'B' ? `${comparedUsername} leads ${h2h.bWins}-${h2h.aWins}` :
                            `It's a tie ${h2h.aWins}-${h2h.bWins}`}
                </div>
            </Section>
        );
    };

    const renderAnimeComparison = () => {
        if (!metrics?.overview) return null;
        const ov = metrics.overview;
        const previewCount = 6;

        const commonPreview = sortAnimeList(metrics.commonAnime || [], 'averageScore', 'desc', titlePreference).slice(0, previewCount);
        const onlyBPreview = sortAnimeList(metrics.onlyBAnime || [], 'averageScore', 'desc', titlePreference).slice(0, previewCount);

        return (
            <Section title="Anime Comparison" icon="📊" id="animecompare">
                <div className="anime-compare-summary">
                    <div className="acs-stat">
                        <span className="acs-count">{ov.userATotalAnime}</span>
                        <span className="acs-label">{username}'s anime</span>
                    </div>
                    <div className="acs-stat acs-common">
                        <span className="acs-count">{ov.commonAnimeCount}</span>
                        <span className="acs-label">Common</span>
                        <span className="acs-percent">
                            <span className="acs-pct-a">{ov.commonPercentOfA}% of {username}</span>
                            <span className="acs-pct-sep">·</span>
                            <span className="acs-pct-b">{ov.commonPercentOfB}% of {comparedUsername}</span>
                        </span>
                    </div>
                    <div className="acs-stat">
                        <span className="acs-count">{ov.userBTotalAnime}</span>
                        <span className="acs-label">{comparedUsername}'s anime</span>
                    </div>
                </div>

                {commonPreview.length > 0 && (
                    <div className="anime-subsection">
                        <div className="anime-subsection-header">
                            <h4>Common Anime</h4>
                            {(metrics.commonAnime?.length || 0) > previewCount && (
                                <button className="view-all-btn" onClick={() => setOverlayData({
                                    title: 'Common Anime',
                                    list: metrics.commonAnime,
                                    showScores: true
                                })}>View All ({metrics.commonAnime.length})</button>
                            )}
                        </div>
                        <div className="compare-anime-grid">
                            {commonPreview.map((anime, i) => (
                                <AnimeCard key={anime.media?.id || i} anime={anime} userAName={username} userBName={comparedUsername} onClick={() => setDetailAnime(anime)} onAdd={(status) => handleAddToList(anime.media?.id, status)} isAdding={addingAnime.has(anime.media?.id)} userAnimeMap={userAnimeMap} onWatch={() => { setSelectedAnimeForWatch(getTitle(anime.media, titlePreference)); setShowWatchModal(true); }} />
                            ))}
                        </div>
                    </div>
                )}

                {onlyBPreview.length > 0 && (
                    <div className="anime-subsection">
                        <div className="anime-subsection-header">
                            <h4>Only {comparedUsername} Has</h4>
                            {(metrics.onlyBAnime?.length || 0) > previewCount && (
                                <button className="view-all-btn" onClick={() => setOverlayData({
                                    title: `Only ${comparedUsername} Has`,
                                    list: metrics.onlyBAnime,
                                    showScores: false
                                })}>View All ({metrics.onlyBAnime.length})</button>
                            )}
                        </div>
                        <div className="compare-anime-grid">
                            {onlyBPreview.map((anime, i) => (
                                <AnimeCard key={anime.media?.id || i} anime={anime} userAName={username} userBName={comparedUsername} showScores={false} onClick={() => setDetailAnime(anime)} onAdd={(status) => handleAddToList(anime.media?.id, status)} isAdding={addingAnime.has(anime.media?.id)} userAnimeMap={userAnimeMap} onWatch={() => { setSelectedAnimeForWatch(getTitle(anime.media, titlePreference)); setShowWatchModal(true); }} />
                            ))}
                        </div>
                    </div>
                )}
            </Section>
        );
    };

    const renderGroupComparison = () => {
        if (!metrics?.groupComparison) return null;
        const gc = metrics.groupComparison;
        if ((!gc.matchedGroups || gc.matchedGroups.length === 0) && (!gc.onlyBGroups || gc.onlyBGroups.length === 0)) return null;

        const groupPreviewCount = 6;
        const visibleGroups = (gc.matchedGroups || []).slice(0, groupPreviewCount);
        const hasMoreGroups = (gc.matchedGroups || []).length > groupPreviewCount;

        const onlyBGroups = gc.onlyBGroups || [];
        const visibleOnlyBGroups = onlyBGroups.slice(0, groupPreviewCount);
        const hasMoreOnlyBGroups = onlyBGroups.length > groupPreviewCount;

        const renderGroupCard = (group, i) => {
            const total = group.totalInGroup || (group.aAnimeCount + group.bAnimeCount - group.commonCount);
            const completionA = total > 0 ? Math.round((group.aAnimeCount / total) * 100) : 0;
            const completionB = total > 0 ? Math.round((group.bAnimeCount / total) * 100) : 0;

            return (
                <div key={i} className="group-card" onClick={() => setGroupDetailData(group)}>
                    <div className="group-card-cover">
                        {group.coverImage ? (
                            <img src={group.coverImage} alt="" loading="lazy" />
                        ) : (
                            <div className="group-card-no-cover">📦</div>
                        )}
                    </div>
                    <div className="group-card-info">
                        <div className="group-card-name" title={group.name}>{group.name}</div>
                        <div className="group-card-counts">
                            <span className="gcc-common">🤝 {group.commonCount} shared</span>
                            <span className="gcc-total">{total} anime total</span>
                        </div>
                        <div className="group-card-bars">
                            <div className="group-bar-row">
                                <span className="gbr-label gbr-a">{username}</span>
                                <div className="gbr-track">
                                    <div className="gbr-fill gbr-fill-a" style={{ width: `${completionA}%` }} />
                                </div>
                                <span className="gbr-count">{group.aAnimeCount}/{total}</span>
                            </div>
                            <div className="group-bar-row">
                                <span className="gbr-label gbr-b">{comparedUsername}</span>
                                <div className="gbr-track">
                                    <div className="gbr-fill gbr-fill-b" style={{ width: `${completionB}%` }} />
                                </div>
                                <span className="gbr-count">{group.bAnimeCount}/{total}</span>
                            </div>
                        </div>
                    </div>
                </div>
            );
        };

        const renderOnlyBGroupCard = (group, i) => {
            const handleClick = () => {
                // Transform only-B group data to match the group detail overlay format
                setGroupDetailData({
                    name: group.name,
                    coverImage: group.coverImage,
                    commonCount: 0,
                    aAnimeCount: 0,
                    bAnimeCount: group.animeCount,
                    totalInGroup: group.animeCount,
                    commonAnime: [],
                    onlyAAnime: [],
                    onlyBAnime: group.anime || []
                });
            };

            return (
                <div key={`onlyb-${i}`} className="group-card" onClick={handleClick}>
                    <div className="group-card-cover">
                        {group.coverImage ? (
                            <img src={group.coverImage} alt="" loading="lazy" />
                        ) : (
                            <div className="group-card-no-cover">📦</div>
                        )}
                    </div>
                    <div className="group-card-info">
                        <div className="group-card-name" title={group.name}>{group.name}</div>
                        <div className="group-card-counts">
                            <span className="gcc-total">🎬 {group.animeCount} anime</span>
                        </div>
                    </div>
                </div>
            );
        };

        return (
            <Section title="Franchise Comparison" icon="📦" id="groups">
                <div className="group-summary-bar">
                    <div className="gsb-item gsb-a">
                        <span className="gsb-count">{gc.totalGroupsA}</span>
                        <span className="gsb-label">{username}'s franchises</span>
                    </div>
                    <div className="gsb-item gsb-match">
                        <span className="gsb-count">{gc.matchedCount}</span>
                        <span className="gsb-label">Shared</span>
                    </div>
                    <div className="gsb-item gsb-b">
                        <span className="gsb-count">{gc.totalGroupsB}</span>
                        <span className="gsb-label">{comparedUsername}'s franchises</span>
                    </div>
                </div>
                {gc.onlyAGroupCount > 0 || gc.onlyBGroupCount > 0 ? (
                    <div className="group-unique-note">
                        {gc.onlyAGroupCount > 0 && <span className="gun-a">{gc.onlyAGroupCount} only in {username}'s list</span>}
                        {gc.onlyBGroupCount > 0 && <span className="gun-b">{gc.onlyBGroupCount} only in {comparedUsername}'s list</span>}
                    </div>
                ) : null}

                {/* Common Franchises */}
                {visibleGroups.length > 0 && (
                    <div className="anime-subsection">
                        <div className="anime-subsection-header">
                            <h4>Common Franchises</h4>
                            {hasMoreGroups && (
                                <button className="view-all-btn" onClick={() => setViewAllGroups(gc.matchedGroups)}>
                                    View All ({gc.matchedGroups.length})
                                </button>
                            )}
                        </div>
                        <div className="group-cards-grid">
                            {visibleGroups.map(renderGroupCard)}
                        </div>
                    </div>
                )}

                {/* Only User B Has */}
                {visibleOnlyBGroups.length > 0 && (
                    <div className="anime-subsection">
                        <div className="anime-subsection-header">
                            <h4>Only {comparedUsername} Has</h4>
                            {hasMoreOnlyBGroups && (
                                <button className="view-all-btn" onClick={() => setViewAllGroups(onlyBGroups.map(g => ({
                                    name: g.name,
                                    coverImage: g.coverImage,
                                    commonCount: 0,
                                    aAnimeCount: 0,
                                    bAnimeCount: g.animeCount,
                                    totalInGroup: g.animeCount,
                                    commonAnime: [],
                                    onlyAAnime: [],
                                    onlyBAnime: g.anime || [],
                                    _isOnlyB: true
                                })))}>
                                    View All ({onlyBGroups.length})
                                </button>
                            )}
                        </div>
                        <div className="group-cards-grid">
                            {visibleOnlyBGroups.map(renderOnlyBGroupCard)}
                        </div>
                    </div>
                )}
            </Section>
        );
    };

    const renderGenreAnalysis = () => {
        if (!metrics?.genreAnalysis) return null;
        const ga = metrics.genreAnalysis;
        const topGenres = ga.genreComparison.slice(0, 12);

        return (
            <Section title="Genre Compatibility" icon="🎭" id="genre">
                <div className="genre-overlap-score">
                    <span className="genre-score-value">{ga.overlapScore}%</span>
                    <span className="genre-score-label">Genre Overlap</span>
                </div>
                <div className="genre-bars">
                    {topGenres.map((g, i) => {
                        const maxCount = Math.max(g.aCount, g.bCount, 1);
                        return (
                            <div key={i} className="genre-bar-row">
                                <div className="genre-bar-label">{g.genre}</div>
                                <div className="genre-bar-track">
                                    <div className="genre-bar-a" style={{ width: `${(g.aCount / maxCount) * 100}%` }}>
                                        <span>{g.aCount}</span>
                                    </div>
                                    <div className="genre-bar-b" style={{ width: `${(g.bCount / maxCount) * 100}%` }}>
                                        <span>{g.bCount}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {ga.divergentGenres.length > 0 && (
                    <div className="unique-genres">
                        <h4>Unique Preferences</h4>
                        <div className="unique-genre-chips">
                            {ga.divergentGenres.map((dg, i) => (
                                <span key={i} className={`unique-genre-chip ${dg.dominantUser === 'A' ? 'chip-a' : 'chip-b'}`}>
                                    {dg.genre} ({dg.dominantUser === 'A' ? username : comparedUsername}: {dg.dominantUser === 'A' ? dg.aPercent : dg.bPercent}%)
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </Section>
        );
    };

    const renderScoreDistribution = () => {
        if (!metrics?.scoreDistribution) return null;
        const sd = metrics.scoreDistribution;
        const maxCount = Math.max(
            ...Object.values(sd.userA),
            ...Object.values(sd.userB),
            1
        );

        return (
            <Section title="Score Distribution" icon="📈" id="scores">
                <div className="score-dist-chart">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(score => (
                        <div key={score} className="score-dist-column">
                            <div className="score-dist-bars">
                                <div className="sdb-a" style={{ height: `${(sd.userA[score] / maxCount) * 100}%` }} title={`${username}: ${sd.userA[score]}`}>
                                    {sd.userA[score] > 0 && <span>{sd.userA[score]}</span>}
                                </div>
                                <div className="sdb-b" style={{ height: `${(sd.userB[score] / maxCount) * 100}%` }} title={`${comparedUsername}: ${sd.userB[score]}`}>
                                    {sd.userB[score] > 0 && <span>{sd.userB[score]}</span>}
                                </div>
                            </div>
                            <div className="score-dist-label">{score}</div>
                        </div>
                    ))}
                </div>
                <div className="score-dist-legend">
                    <span className="sdl-a">■ {username}</span>
                    <span className="sdl-b">■ {comparedUsername}</span>
                </div>
            </Section>
        );
    };

    const renderScoringInsights = () => {
        if (!metrics?.scoringInsights) return null;
        const si = metrics.scoringInsights;

        return (
            <Section title="Scoring Insights" icon="🎯" id="insights">
                <div className="scoring-cards">
                    <div className="scoring-card">
                        <div className="sc-name">{username}</div>
                        <div className="sc-avg">{si.userA.avgScore}</div>
                        <div className="sc-label">Avg Score</div>
                        <div className={`sc-style sc-${si.userA.generosity.toLowerCase()}`}>{si.userA.generosity}</div>
                        <div className="sc-detail">{si.userA.scoredCount} scored · {si.userA.unscored} unscored</div>
                    </div>
                    <div className="scoring-vs">VS</div>
                    <div className="scoring-card">
                        <div className="sc-name">{comparedUsername}</div>
                        <div className="sc-avg">{si.userB.avgScore}</div>
                        <div className="sc-label">Avg Score</div>
                        <div className={`sc-style sc-${si.userB.generosity.toLowerCase()}`}>{si.userB.generosity}</div>
                        <div className="sc-detail">{si.userB.scoredCount} scored · {si.userB.unscored} unscored</div>
                    </div>
                </div>
            </Section>
        );
    };

    const renderStudioPreferences = () => {
        if (!metrics?.studioAnalysis) return null;
        const sa = metrics.studioAnalysis;
        const maxCount = Math.max(...sa.studioComparison.slice(0, 10).flatMap(s => [s.aCount, s.bCount]), 1);

        return (
            <Section title="Studio Preferences" icon="🏢" id="studios">
                <div className="studio-overlap-stat">
                    {sa.commonStudiosCount} shared studios out of {sa.totalStudios} ({sa.overlapPercent}% overlap)
                </div>
                <div className="studio-list">
                    {sa.studioComparison.slice(0, 10).map((s, i) => (
                        <div key={i} className="studio-row">
                            <div className="studio-name">{s.studio}</div>
                            <div className="studio-counts">
                                <span className="studio-cnt-a" style={{ width: `${(s.aCount / maxCount) * 100}%` }}>{s.aCount}</span>
                                <span className="studio-cnt-b" style={{ width: `${(s.bCount / maxCount) * 100}%` }}>{s.bCount}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        );
    };

    const renderTemporalAnalysis = () => {
        if (!metrics?.temporalAnalysis?.decades?.length) return null;
        const decades = metrics.temporalAnalysis.decades;
        const maxCount = Math.max(...decades.flatMap(d => [d.aCount, d.bCount]), 1);

        return (
            <Section title="Timeline / Era Comparison" icon="📅" id="timeline">
                <div className="timeline-bars">
                    {decades.map((d, i) => (
                        <div key={i} className="timeline-row">
                            <div className="timeline-decade">{d.decade}</div>
                            <div className="timeline-bar-track">
                                {d.aCount > 0 && (
                                    <div className="timeline-bar-a" style={{ width: `${(d.aCount / maxCount) * 100}%` }}>
                                        <span>{d.aCount}</span>
                                    </div>
                                )}
                                {d.bCount > 0 && (
                                    <div className="timeline-bar-b" style={{ width: `${(d.bCount / maxCount) * 100}%` }}>
                                        <span>{d.bCount}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        );
    };

    const renderStatusDistribution = () => {
        if (!metrics?.statusDistribution) return null;
        const sd = metrics.statusDistribution;
        const statuses = ['COMPLETED', 'CURRENT', 'PLANNING', 'PAUSED', 'DROPPED'];
        const labels = { COMPLETED: 'Completed', CURRENT: 'Watching', PLANNING: 'Planning', PAUSED: 'Paused', DROPPED: 'Dropped' };
        const icons = { COMPLETED: '✅', CURRENT: '▶️', PLANNING: '📋', PAUSED: '⏸️', DROPPED: '❌' };
        const totalA = Object.values(sd.userA).reduce((s, v) => s + v, 0) || 1;
        const totalB = Object.values(sd.userB).reduce((s, v) => s + v, 0) || 1;

        return (
            <Section title="Status Distribution" icon="📊" id="status">
                <div className="status-comparison">
                    {statuses.map(status => {
                        const aVal = sd.userA[status] || 0;
                        const bVal = sd.userB[status] || 0;
                        const aPct = Math.round((aVal / totalA) * 100);
                        const bPct = Math.round((bVal / totalB) * 100);
                        const total = aVal + bVal;
                        const aWidth = total > 0 ? (aVal / total) * 100 : 50;
                        const bWidth = total > 0 ? (bVal / total) * 100 : 50;
                        return (
                            <div key={status} className="status-row">
                                <div className="status-label">{icons[status]} {labels[status]}</div>
                                <div className="status-bar-track">
                                    {aVal > 0 && <div className="status-bar-a" style={{ width: `${aWidth}%` }}><span>{aVal}</span></div>}
                                    {bVal > 0 && <div className="status-bar-b" style={{ width: `${bWidth}%` }}><span>{bVal}</span></div>}
                                </div>
                                <div className="status-values">
                                    <span className="sv-a">{username}: {aPct}%</span>
                                    <span className="sv-b">{comparedUsername}: {bPct}%</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Section>
        );
    };

    const renderHotTakes = () => {
        if (!metrics?.hotTakes?.length) return null;

        return (
            <Section title="Hot Takes 🔥" icon="🌶️" id="hottakes">
                <p className="section-subtitle">Biggest score disagreements (≥3 points)</p>
                <div className="hot-takes-grid">
                    {metrics.hotTakes.slice(0, 8).map((anime, i) => (
                        <div key={anime.media?.id || i} className="hot-take-card" onClick={() => setDetailAnime(anime)}>
                            <div className="htc-cover">
                                {anime.media?.coverImage?.large ? (
                                    <img src={anime.media.coverImage.extraLarge || anime.media.coverImage.large} alt="" loading="lazy" />
                                ) : <div className="htc-no-cover">?</div>}
                            </div>
                            <div className="htc-info">
                                <div className="htc-title">{getTitle(anime.media, titlePreference)}</div>
                                <div className="htc-scores">
                                    <span className="htc-a">{username}: {anime.userAScore}</span>
                                    <span className="htc-b">{comparedUsername}: {anime.userBScore}</span>
                                </div>

                            </div>
                        </div>
                    ))}
                </div>
            </Section>
        );
    };

    const renderRatingCorrelation = () => {
        if (!metrics?.ratingCorrelation) return null;
        const rc = metrics.ratingCorrelation;

        return (
            <Section title="Rating Correlation" icon="📐" id="correlation">
                <div className="correlation-display">
                    <div className="corr-value" style={{ color: rc.correlation > 0.5 ? 'var(--green)' : rc.correlation > 0 ? 'var(--yellow)' : 'var(--red)' }}>
                        {rc.correlation > 0 ? '+' : ''}{rc.correlation}
                    </div>
                    <div className="corr-label">
                        Pearson correlation ({rc.sampleSize} anime with mutual scores)
                    </div>

                </div>
            </Section>
        );
    };

    // ============================================================
    // MAIN RENDER
    // ============================================================
    return (
        <div className="compare-container">
            {/* Search + Saved */}
            <div className="compare-top">
                <div className="compare-search-section">
                    <h2>Compare Anime Lists</h2>
                    <div className="compare-search-row">
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Enter AniList username..."
                            value={searchUsername}
                            onChange={e => setSearchUsername(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={isComparing}
                            className="compare-search-input"
                        />
                        <button
                            onClick={() => handleCompare()}
                            disabled={isComparing || !searchUsername.trim()}
                            className="compare-search-btn"
                        >
                            {isComparing ? '⏳ Comparing...' : '⚔️ Compare'}
                        </button>
                    </div>
                    {error && <div className="compare-error">{error}</div>}
                </div>

                {/* Most recent saved comparison only, rest behind overlay */}
                {mostRecentSaved && (
                    <div className="compare-saved-section">
                        <h3>Recent Comparison</h3>
                        <div
                            className={`saved-item ${comparedUsername === mostRecentSaved.userBUsername ? 'active' : ''}`}
                            onClick={() => handleLoadSaved(mostRecentSaved.userBUsername)}
                        >
                            <div className="saved-avatar">
                                {mostRecentSaved.userBAvatar ? (
                                    <img src={mostRecentSaved.userBAvatar} alt="" />
                                ) : (
                                    <div className="saved-avatar-placeholder">{mostRecentSaved.userBUsername[0]?.toUpperCase()}</div>
                                )}
                            </div>
                            <div className="saved-info">
                                <div className="saved-name">{mostRecentSaved.userBUsername}</div>
                                <div className="saved-meta">
                                    {mostRecentSaved.summary?.compatibilityScore}% compat · {mostRecentSaved.summary?.commonAnimeCount} common
                                </div>
                            </div>
                            <button className="saved-delete" onClick={(e) => handleDeleteSaved(mostRecentSaved.userBUsername, e)} title="Delete">✕</button>
                        </div>
                        {hasMoreSaved && (
                            <button className="view-all-saved-btn" onClick={() => setShowSavedOverlay(true)}>
                                View All ({savedComparisons.length})
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Loading state */}
            {isComparing && (
                <div className="compare-loading">
                    <div className="compare-loading-spinner" />
                    <p>Comparing anime lists...</p>
                </div>
            )}

            {/* Comparison results */}
            {metrics && !isComparing && (
                <div className="compare-results">
                    <div className="compare-action-bar">
                        <span className="cab-label">Comparing with <strong>{comparedUsername}</strong></span>
                        <div className="cab-actions">
                            <button className="cab-btn" onClick={handleRefresh} title="Refresh comparison">🔄 Refresh</button>
                            <button className="cab-btn cab-new" onClick={() => { setComparisonData(null); setComparedUsername(''); searchInputRef.current?.focus(); }}>➕ New</button>
                        </div>
                    </div>

                    {renderCompatibilityHero()}
                    {renderGroupComparison()}
                    {renderAnimeComparison()}
                    {renderHeadToHead()}
                    {renderGenreAnalysis()}
                    {renderScoreDistribution()}
                    {renderScoringInsights()}
                    {renderRatingCorrelation()}
                    {renderStudioPreferences()}
                    {renderTemporalAnalysis()}
                    {renderStatusDistribution()}
                    {renderHotTakes()}
                </div>
            )}

            {/* Empty state */}
            {!metrics && !isComparing && (
                <div className="compare-empty-state">
                    <div className="compare-empty-icon">⚔️</div>
                    <h2>Ready to Compare</h2>
                    <p>Enter an AniList username above to compare your anime taste, or click a recent comparison to view it again.</p>
                </div>
            )}

            {/* View All anime overlay */}
            {overlayData && (
                <AnimeListOverlay
                    title={overlayData.title}
                    animeList={overlayData.list}
                    onClose={() => setOverlayData(null)}
                    userAName={username}
                    userBName={comparedUsername}
                    showScores={overlayData.showScores}
                    onAdd={handleAddToList}
                    addingAnime={addingAnime}
                    userAnimeMap={userAnimeMap}
                    onWatch={(animeName) => { setSelectedAnimeForWatch(animeName); setShowWatchModal(true); }}
                />
            )}

            {/* Saved comparisons overlay */}
            {showSavedOverlay && (
                <SavedComparisonsOverlay
                    comparisons={savedComparisons}
                    onLoad={handleLoadSaved}
                    onDelete={handleDeleteSaved}
                    activeUsername={comparedUsername}
                    onClose={() => setShowSavedOverlay(false)}
                />
            )}

            {/* View All Franchises overlay */}
            {viewAllGroups && (
                <div className="compare-overlay-backdrop" onClick={() => setViewAllGroups(null)}>
                    <div className="compare-overlay compare-overlay-wide" onClick={e => e.stopPropagation()}>
                        <div className="compare-overlay-header">
                            <h3>📦 {viewAllGroups[0]?._isOnlyB ? `Only ${comparedUsername} Has` : 'All Shared Franchises'} ({viewAllGroups.length})</h3>
                            <button className="compare-overlay-close" onClick={() => setViewAllGroups(null)}>✕</button>
                        </div>
                        <div className="group-detail-content">
                            <div className="group-cards-grid">
                                {viewAllGroups.map((group, i) => {
                                    const isOnlyB = group._isOnlyB;
                                    const total = group.totalInGroup || (group.aAnimeCount + group.bAnimeCount - group.commonCount);
                                    return (
                                        <div key={i} className="group-card" onClick={() => setGroupDetailData(group)}>
                                            <div className="group-card-cover">
                                                {group.coverImage ? (
                                                    <img src={group.coverImage} alt="" loading="lazy" />
                                                ) : (
                                                    <div className="group-card-no-cover">📦</div>
                                                )}
                                            </div>
                                            <div className="group-card-info">
                                                <div className="group-card-name" title={group.name}>{group.name}</div>
                                                <div className="group-card-counts">
                                                    {isOnlyB ? (
                                                        <span className="gcc-total">🎬 {group.bAnimeCount} anime</span>
                                                    ) : (
                                                        <>
                                                            <span className="gcc-common">🤝 {group.commonCount} shared</span>
                                                            <span className="gcc-total">{total} anime total</span>
                                                        </>
                                                    )}
                                                </div>
                                                {!isOnlyB && (
                                                    <div className="group-card-bars">
                                                        <div className="group-bar-row">
                                                            <span className="gbr-label gbr-a">{username}</span>
                                                            <div className="gbr-track">
                                                                <div className="gbr-fill gbr-fill-a" style={{ width: `${total > 0 ? Math.round((group.aAnimeCount / total) * 100) : 0}%` }} />
                                                            </div>
                                                            <span className="gbr-count">{group.aAnimeCount}/{total}</span>
                                                        </div>
                                                        <div className="group-bar-row">
                                                            <span className="gbr-label gbr-b">{comparedUsername}</span>
                                                            <div className="gbr-track">
                                                                <div className="gbr-fill gbr-fill-b" style={{ width: `${total > 0 ? Math.round((group.bAnimeCount / total) * 100) : 0}%` }} />
                                                            </div>
                                                            <span className="gbr-count">{group.bAnimeCount}/{total}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Group detail overlay — stacks on top of View All when opened from there */}
            {groupDetailData && (
                <div className="compare-overlay-backdrop" onClick={() => setGroupDetailData(null)}>
                    <div className="compare-overlay compare-overlay-wide" onClick={e => e.stopPropagation()}>
                        <div className="compare-overlay-header">
                            <h3>📦 {groupDetailData.name}</h3>
                            <button className="compare-overlay-close" onClick={() => setGroupDetailData(null)}>✕</button>
                        </div>
                        <div className="group-detail-content">
                            <div className="group-detail-summary">
                                <span className="gds-common">🤝 {groupDetailData.commonCount} shared</span>
                                <span className="gds-a">🔵 {groupDetailData.onlyAAnime?.length || 0} only {username}</span>
                                <span className="gds-b">🟡 {groupDetailData.onlyBAnime?.length || 0} only {comparedUsername}</span>
                            </div>

                            {groupDetailData.commonAnime?.length > 0 && (
                                <div className="group-detail-section">
                                    <h4 className="gds-title gds-title-common">🤝 Both Have ({groupDetailData.commonAnime.length})</h4>
                                    <div className="compare-anime-grid">
                                        {groupDetailData.commonAnime.map((anime, i) => (
                                            <AnimeCard key={anime.media?.id || i} anime={anime} userAName={username} userBName={comparedUsername} showScores={true} onClick={() => setDetailAnime(anime)} onAdd={(status) => handleAddToList(anime.media?.id, status)} isAdding={addingAnime.has(anime.media?.id)} userAnimeMap={userAnimeMap} onWatch={() => { setSelectedAnimeForWatch(getTitle(anime.media, titlePreference)); setShowWatchModal(true); }} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {groupDetailData.onlyAAnime?.length > 0 && (
                                <div className="group-detail-section">
                                    <h4 className="gds-title gds-title-a">🔵 Only {username} Has ({groupDetailData.onlyAAnime.length})</h4>
                                    <div className="compare-anime-grid">
                                        {groupDetailData.onlyAAnime.map((anime, i) => (
                                            <AnimeCard key={anime.media?.id || i} anime={anime} userAName={username} userBName={comparedUsername} showScores={false} onClick={() => setDetailAnime(anime)} onAdd={(status) => handleAddToList(anime.media?.id, status)} isAdding={addingAnime.has(anime.media?.id)} userAnimeMap={userAnimeMap} onWatch={() => { setSelectedAnimeForWatch(getTitle(anime.media, titlePreference)); setShowWatchModal(true); }} />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {groupDetailData.onlyBAnime?.length > 0 && (
                                <div className="group-detail-section">
                                    <h4 className="gds-title gds-title-b">🟡 Only {comparedUsername} Has ({groupDetailData.onlyBAnime.length})</h4>
                                    <div className="compare-anime-grid">
                                        {groupDetailData.onlyBAnime.map((anime, i) => (
                                            <AnimeCard key={anime.media?.id || i} anime={anime} userAName={username} userBName={comparedUsername} showScores={false} onClick={() => setDetailAnime(anime)} onAdd={(status) => handleAddToList(anime.media?.id, status)} isAdding={addingAnime.has(anime.media?.id)} userAnimeMap={userAnimeMap} onWatch={() => { setSelectedAnimeForWatch(getTitle(anime.media, titlePreference)); setShowWatchModal(true); }} />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Anime detail modal — rendered LAST so it appears above all overlays */}
            {detailAnime && (
                <AnimeDetailModal
                    anime={detailAnime}
                    onClose={() => setDetailAnime(null)}
                    userAName={username}
                    userBName={comparedUsername}
                    onAdd={(status) => handleAddToList(detailAnime.media?.id, status)}
                    isAdding={addingAnime.has(detailAnime.media?.id)}
                    userStatus={userAnimeMap.get(detailAnime.media?.id)}
                    onWatch={() => { setSelectedAnimeForWatch(getTitle(detailAnime.media, titlePreference)); setShowWatchModal(true); }}
                />
            )}

            {/* Watch Links Modal */}
            <SiteLinksModal
                isOpen={showWatchModal}
                onClose={() => { setShowWatchModal(false); setSelectedAnimeForWatch(null); }}
                links={watchLinks}
                animeName={selectedAnimeForWatch || ''}
                type="watch"
            />
        </div>
    );
};

// ============================================================
// Section wrapper component
// ============================================================
const Section = ({ title, icon, id, children }) => {
    const [collapsed, setCollapsed] = useState(false);

    return (
        <div className={`compare-section ${collapsed ? 'collapsed' : ''}`} id={`section-${id}`}>
            <div className="compare-section-header" onClick={() => setCollapsed(!collapsed)}>
                <h3><span className="section-icon">{icon}</span> {title}</h3>
                <span className="section-chevron">{collapsed ? '▸' : '▾'}</span>
            </div>
            {!collapsed && <div className="compare-section-body">{children}</div>}
        </div>
    );
};

export default Compare;
